import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import useFeature from 'services/features/useFeature';
import useTinyAccounts from 'services/account/useTinyAccounts';
import { useTinyAccountForHome } from 'hooks/useTinyAccountForHome';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import { getHomePageTinyAccountId } from 'store/home';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useAnalyticsSource } from 'contexts/analytics/useAnalyticsSource';
import TinyAccount from 'types/TinyAccount';

import { TinyAccountSelector, Typography } from 'components';
import { KitsSection } from './KitsSection/KitsSection';
import { NewFamilyMemberCard } from '../NewFamilyMemberCard/NewFamilyMemberCard';
import { RecommendedForYou } from './RecommendedForYou/RecommendedForYou';
import { ProcessingYourSample } from './ProcessingYourSample/ProcessingYourSample';
import { ConsultNoteReminder } from './ConsultNoteReminder/ConsultNoteReminder';
import { MembershipSection } from './MembershipSection/MembershipSection';
import { ConsultReminder } from './ConsultReminder/ConsultReminder';
import { HomeNextSteps } from './HomeNextSteps/HomeNextSteps';
import { HomeActions } from './HomeActions/HomeActions';
import { ReferralWidget } from './ReferralWidget/ReferralWidget';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';

import styles from './HomeContent.module.css';
import PartnerRecsBanner from './PartnerRecsBanner/PartnerRecsBanner';

const WelcomeSection = ({
  tinyAccount,
}: {
  tinyAccount?: TinyAccount | null;
}) => {
  if (!tinyAccount) return null;
  return (
    <div className={styles.welcomeSection}>
      <div className='flex flex-column gap-2'>
        <Typography variant='heading-2xl'>
          Welcome, {tinyAccount?.first_name}
        </Typography>
        <Typography variant='heading-l'>
          Congrats on prioritizing your wellness!
        </Typography>
      </div>
      <div className='flex flex-column gap-2'>
        <Typography variant='body-s' serif>
          {`Everyone’s journey is unique, and we’re here to support you each step
          of the way. You can manage everything right here —from your test
          results to your Action Plan to booking a session with your microbiome
          specialist. Feel free to reach out if you have any questions, `}
          <span className={styles.underline}>hello@tinyhealth.com</span>.
        </Typography>
      </div>
    </div>
  );
};

export const HomeContent = () => {
  const navigate = useNavigate();
  const tinyAccountId = useSelector(getHomePageTinyAccountId);
  const { tinyAccount, kits, latestKit } = useTinyAccountForHome(tinyAccountId);
  const homePageTinyAccountId = useSelector(getHomePageTinyAccountId);
  const { tinyAccounts } = useTinyAccounts();
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs(latestKit);
  const { isFeatureEnabled: showGroupCalls } = useFeature(
    'consult_group_calls',
  );

  const showWelcomeSection = useMemo(() => {
    return !kits?.length;
  }, [kits]);

  const { trackEventOnce } = useAnalytics();
  const homeViewedSource = useAnalyticsSource();
  useEffect(() => {
    if (!tinyAccountId) return;
    trackEventOnce('Home Viewed', tinyAccountId, { source: homeViewedSource });
  }, [tinyAccountId, homeViewedSource, trackEventOnce]);

  const tinyAccountSelected = useCallback(
    async (tinyAccount?: Partial<TinyAccount>) => {
      if (tinyAccount?.id) {
        navigate(`/`, { state: { tinyAccountId: tinyAccount.id } });
      }
    },
    [navigate],
  );

  return (
    <>
      {showGroupCalls && !showWelcomeSection && (
        <ConsultReminder className='mt-3 hide-on-mobile' />
      )}
      <TwoColumnGrid className={showWelcomeSection ? styles.welcomeLayout : ''}>
        <TwoColumnGrid.Wide mobileOrder={2}>
          {showWelcomeSection ? (
            <WelcomeSection tinyAccount={tinyAccount} />
          ) : (
            <div className={styles.contentContainer}>
              {!showGroupCalls && (
                <ConsultReminder className='hide-on-mobile' />
              )}
              <ConsultNoteReminder className='hide-on-mobile' />
              <ProcessingYourSample />
              <PartnerRecsBanner />
              <KitsSection tinyAccountId={tinyAccountId} />
              <NewFamilyMemberCard />
              <HomeActions kit={latestKit} />
              <MembershipSection tinyAccountId={tinyAccountId} />
            </div>
          )}
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim isSticky mobileOrder={1}>
          {!!tinyAccounts?.length && tinyAccounts.length > 1 && (
            <div className={styles.forceSelectorStyles}>
              <TinyAccountSelector
                onSelection={tinyAccountSelected}
                currentTinyAccountId={tinyAccountId ?? homePageTinyAccountId}
                withProfile
              />
            </div>
          )}
          <div className={styles.nextStepsContainer}>
            <div className='pt-2'>
              <div className='flex flex-column gap-1'>
                <ConsultReminder className='hide-on-desktop' />
                <ConsultNoteReminder className='hide-on-desktop' />
              </div>
              <HomeNextSteps />
            </div>
          </div>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
      <RecommendedForYou tinyAccountId={tinyAccountId} />

      {!portalConfig.hide_upsells && !isPortalConfigLoading && (
        <ReferralWidget />
      )}
    </>
  );
};
